import * as floorView from './floor-view';
import * as directionalLightingView from './directional-lighting-view';
import BaseView, { DomainModel3D } from './base-view';
import DomainModel from '../domain-model';
import ReadySignal from '../control-flow/ready-signal';
import SetMap from '../mem/set-map';
import WallView from './wall-view';
import mfac from '../material-factory';
import pc from '../parametric-cache';
import { Material, Room } from '../domain';
import { Mesh } from 'three';
import { ONE_DEGREE_IN_RADIANS, TWO_PI } from '../math/constants';
import { RoomViewConfig, roomViewDefaults, RoomViewOptions } from './views-config';
import { angleBetweenWalls } from '../controllers/wall-controllers';
import { approx } from '../measurement';
import { pairwise } from '../control-flow/pairwise';
import { update, clone } from "../json/optional-properties";

const rot90 = (x: number, y: number) => [-y, x];

// Adapted from https://en.wikipedia.org/wiki/Intersection_(geometry)#Two_lines
// xs, ys is the intersection of two lines a1*x + b1*y = c1, and
// a2*x + b2*y = c2. In our case, b1 and b2 are always one. The general formula
// are:
// xs = (c1*b2 - c2*b1) / (a1*b2 - a2*b1)
// ys = (a1*c2 - a2*c1) / (a1*b2 - a2*b1)
const intersect = (
    a1: number, c1: number, a2: number, c2: number): [number, number] =>
{
    return [
        (c1 - c2) / (a1 - a2),
        (a1*c2 - a2*c1) / (a1 - a2)
    ];
}


/**
 * Default maps take a constructor function as an argument and will return a
 * new instance when getting a non-existent value from the map.
 */
export class DefaultMap<K, V> extends Map<K, V> {

    default: V;

    constructor(value: V) {
        super();
        this.default = value;
    }

    get(key: K): V {
        if(this.has(key)) {
            return super.get(key);
        } else {
            return this.default;
        }
    }
}

export default class RoomView extends BaseView {

    declare model: Room;

    walls: WallView[];

    // Maps wall.wallOrder to its visibility flag. We need this because calls
    // to set visibility may arrive before we have completely constructed the
    // scene.
    visibleByWallOrder: DefaultMap<number, boolean> = new DefaultMap(true);
    fillersByWallOrder: SetMap<number, Mesh> = new SetMap();

    config: RoomViewConfig = roomViewDefaults;

    constructor(model: DomainModel3D, options: RoomViewOptions = {}) {
        super(model);
    
        this.walls = [];

        update(this.config, options);
        if(options.standardHeights) {
            this.config.standardHeights = clone(options.standardHeights);
        }

        this.init();

        this.onReady.name = 'RoomView';
    }

    make() {
        // Creating all the materials here should cause any network requests
        // to be initiated now. This is intended to improve the accuracy of
        // the three.js LoadingManager reporting that everything has completed
        // loading. That event can be reported earlier than we want when
        // loading is initiated by another loading handler.
        this.model.traverse((model: DomainModel) => {
            if(model instanceof Material) {
                try {
                    mfac.makefrom(model);
                } catch(error) {
                    console.debug(error);
                }
            }
        });

        // Most of the work here is setting up the corners between walls. Each
        // WallView makes its own mesh for its inner width, but the RoomView
        // creates the fillers between walls so that wall themselves do not
        // need to know about their neighbors.

        let last: number = Infinity;
        for(let wall of this.model.walls) {
            const hideStart = approx(Math.PI/2, wall.rotation.y - last);
            const view = new WallView(wall, {
                standardHeights: this.config.standardHeights,
                hideStart,
            });
            this.walls.push(view);
            this.add(view);

            last = wall.rotation.y;
        }

        if(this.model.walls.length > 0) {
            // Iterate through the walls in adjacent pairs
            for(const [a, b] of pairwise(this.model.walls)) {
                // ignore corners adjacent to islands or invisible walls
                if(a.island || b.island) {
                    continue;
                }
                if(!(a.visible && b.visible)) {
                    continue;
                }


                // angleBetweenWalls gives us the interior angle measure as
                // a positive number between 0 and 2*Math.PI
                const angle = angleBetweenWalls(a, b);
                if(angle < ONE_DEGREE_IN_RADIANS
                    || angle > (TWO_PI - ONE_DEGREE_IN_RADIANS))
                {
                    continue;
                }
                else if(angle < Math.PI) {
                    const
                        height = b.heights[0].height,
                        [ax, ay] = rot90(
                            Math.cos(a.rotation.y)*4.5,
                            Math.sin(a.rotation.y)*4.5
                        ),
                        [bx, by] = rot90(
                            Math.cos(b.rotation.y)*4.5,
                            Math.sin(b.rotation.y)*4.5
                        ),
                    // the slopes of a and b
                    am = Math.tan(a.rotation.y),
                    bm = Math.tan(b.rotation.y),

                    // solve the familiar `y = mx + b` for b given known
                    // points. In terms of our variables, this means solving
                    // `ay = am*ax + ac` for ac. The ac variable becomes a
                    // parameter to the `intersect` function which uses the
                    // general form of a line `ay + bx + c = 0` to solve for
                    // a two line intersection.
                    ac = -am*ax + ay,
                    bc = -bm*bx + by,
                    // c for "corner"
                    [cx, cy] = intersect(-am, ac, -bm, bc);

                    const cornerReady = new ReadySignal('WallCornerFill');
                    this.onReady.depends(cornerReady);
                    // Note that this is an async procedure so wall visibility
                    // may have changed when this runs even though the rest of
                    // the make() method runs inside the constructor.
                    pc.make('WallCornerFill', height, ax, ay, bx, by, cx, cy)
                        .then(geo => {
                            const mesh = new Mesh(geo, mfac.makefrom(b.material))
                            mesh.castShadow = mesh.receiveShadow = true;
                            mesh.position.set(
                                b.position.x, b.position.y, b.position.z);
                            this.fillersByWallOrder.add(a.wallOrder, mesh);
                            this.fillersByWallOrder.add(b.wallOrder, mesh);
                            this.add(mesh);

                            // Both wall's flags must be true for the corner
                            // filler to be visible
                            if(!((this.visibleByWallOrder.get(a.wallOrder))
                                && (this.visibleByWallOrder.get(b.wallOrder))))
                            {
                                mesh.visible = false;
                            }
                            cornerReady.trigger();
                        });
                }
            }
        }

        if(this.config.tempIncludeFloor) {
            this.add(floorView.get(this.model));
        }
        if(this.config.tempIncludeGlobalLighting) {
            this.add(directionalLightingView.get(this.model));
        }
        this.onReady.trigger();
    }

    bind() {
        // nothing to do here ... the fillers won't really work if the walls
        // move dynamically. For now we don't anticipate the floor plan
        // changing in a live 3D view.
    }

    /**
     * Searches the room's walls for a wall with the given `wallOrder` and sets
     * the view's visibility to `isVisible`. This will make the wall and all of
     * its children invisible. Note that the Wall.visible domain model property
     * is used for making only the wall mesh invisible, but it will still
     * display the wall's children. The domain model property supports islands
     * and peninsulas, not the interactive toggling in the UI.
    */
    setWallViewVisible(wallOrder: number, isVisible: boolean) {
        this.visibleByWallOrder.set(wallOrder, isVisible);
        for(const wall of this.walls) {
            if(wall.model.wallOrder == wallOrder) {
                wall.visible = isVisible;
            }
        }
        this.updateFillerVisibility();
    }

    /**
     * This resets all fillers to visible and then hides those that are
     * adjacent to a hidden wall. This approach produces some churn in flipping
     * the visible flag for the objects more than necessary, but the simplicity
     * of the approach is worth it.
     */
    updateFillerVisibility() {
        // Show all the fillers
        for(const filler of this.fillersByWallOrder.valuesFlat()) {
            filler.visible = true;
        }
        // Hide all fillers adjacent to an hidden wall
        for(const [wallOrder, isVisible] of this.visibleByWallOrder.entries()) {
            if(!isVisible && this.fillersByWallOrder.has(wallOrder)) {
                for(const filler of this.fillersByWallOrder.get(wallOrder)) {
                    filler.visible = false;
                }
            }
        }
    }

    /**
     * This must be executed by the render loop, usually by adding this to the
     * onBeforeRender signal on the RenderLoop class.
     */
    updateFunctionBasedViews = () => {
        if(this.config.tempIncludeFloor) {
            floorView.update(this.model, this.config.floor);
        }
        if(this.config.tempIncludeGlobalLighting) {
            directionalLightingView.update(this.model, this.config.directionalLighting);
        }
    };
}
